/**
 * Environment configuration validation utilities
 */

import type { EnvironmentConfig, ValidationResult } from './types';

/**
 * Validate URL format
 */
export function isValidUrl(url: string): boolean {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

/**
 * Validate environment configuration
 */
export function validateConfig(config: EnvironmentConfig): ValidationResult {
  const errors: string[] = [];

  // Validate environment name
  if (!['local', 'dev', 'prod'].includes(config.name)) {
    errors.push(`Invalid environment name: ${config.name}. Must be 'local', 'dev', or 'prod'.`);
  }

  // Skip Cognito validation for mock environment
  if (config.isMock) {
    return { valid: errors.length === 0, errors };
  }

  // Validate Cognito authority URL
  if (!config.cognitoAuthority) {
    errors.push('Cognito authority URL is required');
  } else if (!isValidUrl(config.cognitoAuthority)) {
    errors.push(`Invalid Cognito authority URL: ${config.cognitoAuthority}`);
  }

  // Validate client ID
  if (!config.cognitoClientId) {
    errors.push('Cognito client ID is required');
  }

  // Validate OAuth scope includes "openid"
  if (!config.scope.includes('openid')) {
    errors.push('OAuth scope must include "openid" for OIDC compliance');
  }

  // Validate HTTPS for production
  if (config.name === 'prod') {
    if (config.apiUrl && !config.apiUrl.startsWith('https://')) {
      errors.push('Production API URL must use HTTPS');
    }
    if (config.cognitoAuthority && !config.cognitoAuthority.startsWith('https://')) {
      errors.push('Production Cognito authority must use HTTPS');
    }
  }

  // Validate API URL format
  if (config.apiUrl && !isValidUrl(config.apiUrl)) {
    errors.push(`Invalid API URL: ${config.apiUrl}`);
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Validate required environment variables are present
 */
export function validateEnvVars(envName: string, envVars: Record<string, string | undefined>): ValidationResult {
  const errors: string[] = [];

  // Local environment doesn't require Cognito variables
  if (envName === 'local') {
    return { valid: true, errors: [] };
  }

  // Development environment validation
  if (envName === 'dev') {
    const required = [
      'VITE_DEV_COGNITO_AUTHORITY',
      'VITE_DEV_COGNITO_CLIENT_ID',
      'VITE_DEV_API_URL',
    ];

    for (const key of required) {
      if (!envVars[key]) {
        errors.push(`Missing required environment variable: ${key}`);
      }
    }
  }

  // Production environment validation
  if (envName === 'prod') {
    const required = [
      'VITE_PROD_COGNITO_AUTHORITY',
      'VITE_PROD_COGNITO_CLIENT_ID',
      'VITE_PROD_API_URL',
    ];

    for (const key of required) {
      if (!envVars[key]) {
        errors.push(`Missing required environment variable: ${key}`);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Get helpful error message for configuration errors
 */
export function getErrorMessage(errors: string[]): string {
  return [
    'Configuration Error:',
    '',
    ...errors.map(err => `  • ${err}`),
    '',
    'To fix:',
    '  1. Check your .env file exists',
    '  2. Verify all required variables are set',
    '  3. See .env.example for complete configuration template',
    '  4. Restart the frontend container after changes',
  ].join('\n');
}
